import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { ACTION_MODEL } from '../../../../../Helpers/ActionModel';
import HandleChange from '../../../../../Helpers/HandleChange';
import { useParamsContextProvider } from '../../../../../hooks/ParamsContextProvider';
import {
    GetColorFoodie,
    GetFirstFoodieSetting,
    UpdateFoodieSettingColor,
    UpdateSettingsRestrictions
} from '../../../../../reduxToolkit/Foodie/FoodieApi';
import {
    FOODIE_SETTING_MODEL,
    IS_BASE_COST_NAME, IS_CATALOGUE_COST_NAME,
    IS_MEAL_TIME_COST_NAME, MIN_MEAL_COST,
    MODEL_COLOR
} from '../Models/Model';
import { DECIMAL_REGEX } from '../../../../../Apis/functions';

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * @param {Object} color - Current color configuration model.
 * @param {Function} setColor - Setter for the color model.
 *
 * @returns {{
 *   handleChange: Function,
 *   item: Object,
 *   onChange: Function,
 *   emptyFields: boolean,
 *   pricePlaceholder: string,
 *   isValidSelectionMenuDelay: boolean,
 *   isValidCost: boolean,
 *   isValidCostText: string,
 *   isValidSubsidy: boolean,
 *   isValidSubsidyText: string,
 *   isValidExtraMealSubsidy: boolean,
 *   isValidExtraMealSubsidyText: string,
 *   onCostChange: Function
 * }}
 */
const SettingsApp = ({ color, setColor }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { paramsContext, setParamsContext } = useParamsContextProvider();
    const { handleChange } = HandleChange({ model: color, setModel: setColor });

    const [item, setItem] = useState({ ...FOODIE_SETTING_MODEL });
    const { handleChange: onChange } = HandleChange({ model: item, setModel: setItem });
    const [emptyFields, setEmptyFields] = useState(false);
    const [modified, setModified] = useState(0);
    const [isValidSelectionMenuDelay, setIsValidSelectionMenuDelay] = useState(true);
    const [isValidCost, setIsValidCost] = useState(true);
    const [isValidCostText, setIsValidCostText] = useState('');
    const [isValidSubsidy, setIsValidSubsidy] = useState(true);
    const [isValidSubsidyText, setIsValidSubsidyText] = useState('');
    const [isValidExtraMealSubsidy, setIsValidExtraMealSubsidy] = useState(true);
    const [isValidExtraMealSubsidyText, setIsValidExtraMealSubsidyText] = useState('');
    const pricePlaceholder = t('abbreviation_example') + " $20";

    /**
     * useEffect for get data color and setting
     */
    useEffect(() => {
        dispatch(GetFirstFoodieSetting()).then(({ payload: { data: { data, success } } }) => {
            if(success){
                setItem(FOODIE_SETTING_MODEL.set(data));
            }
        });
        dispatch(GetColorFoodie()).then(({ payload: { data: { data } } }) => {
            setColor({ ...MODEL_COLOR, ...data })
        });
    }, [modified]);

    /**
     * handle to update information
     */
    useEffect(() => {
        if (paramsContext && paramsContext.btnUpdate === ACTION_MODEL.UPDATE) {
            handleUpdate();
        }
    }, [paramsContext]);


    /**
     * api for update infromacion
     * @returns {Promise<void>}
     */
    const handleUpdate = async () => {
        let bodyRestriction = item.toAddEdit();
        let bodyColor = color.toAddEdit();

        if (!item.isValid() || !color.isValid() || (item.isBaseCost && !isValidCost) || !isValidSubsidy || (item.isExtraMealSubsidy && !isValidExtraMealSubsidy)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }
        setEmptyFields(false);

        await dispatch(UpdateFoodieSettingColor(bodyColor));
        await dispatch(UpdateSettingsRestrictions(bodyRestriction));
        setModified(prev => prev + 1);
        if (setParamsContext) {
            setParamsContext(prev => ({
                ...prev,
                btnUpdate: null
            }));
        }
        toast.success(t('has_been_updated_successfully'));
    }

    useEffect(() => {
        if (!item.isExtraMealSubsidy) {
            setItem((prevState) => ({ ...prevState, extraMealSubsidy: 0 }));
            setIsValidExtraMealSubsidy(true);
            setIsValidExtraMealSubsidyText('');
        }
    }, [item.isExtraMealSubsidy]);

    useEffect(() => {
        if(parseInt(item.selectionMenuDelay) === 0) setIsValidSelectionMenuDelay(false);
        else setIsValidSelectionMenuDelay(true);
    }, [item.selectionMenuDelay]);

    /**
     * Validates a decimal field value and updates its validity state.
     * Rejects trailing dots and values below MIN_MEAL_COST.min.
     * Passing an empty string resets the field to valid.
     *
     * @param {string} rawValue - Raw string value from the input.
     * @param {Function} setValid - Setter for the boolean validity state.
     * @param {Function} setText - Setter for the validation error message key.
     */
    const validateDecimalField = (rawValue, setValid, setText) => {
        if (rawValue === '') { setValid(true); return; }
        if (!DECIMAL_REGEX.test(rawValue)) {
            setText('invalid_format');
            setValid(false);
            return;
        }
        const val = Number(rawValue);
        setText(t('it_must_be_greater_than_or_equal_to', { min: MIN_MEAL_COST.min }));
        setValid(!isNaN(val) && val >= MIN_MEAL_COST.min);
    };

    /** Validates subsidy whenever its value changes. */
    useEffect(() => {
        validateDecimalField(item.subsidy?.toString() ?? '', setIsValidSubsidy, setIsValidSubsidyText);
    }, [item.subsidy]);

    /** Validates extra meal subsidy only when the custom subsidy option is enabled. */
    useEffect(() => {
        if (!item.isExtraMealSubsidy) return;
        validateDecimalField(item.extraMealSubsidy?.toString() ?? '', setIsValidExtraMealSubsidy, setIsValidExtraMealSubsidyText);
    }, [item.extraMealSubsidy]);

    /** Validates base cost only when the general price option is enabled. */
    useEffect(() => {
        if (!item.isBaseCost) return;
        validateDecimalField(item.baseCost?.toString() ?? '', setIsValidCost, setIsValidCostText);
    }, [item.baseCost]);


    /**
     * Handles cost type changes from the UI selection.
     *
     * Depending on the selected option, it updates the item state to:
     * - Enable meal time cost
     * - Enable base cost
     * - Or reset both (catalogue/default option)
     *
     * It also resets the base cost value to 0 whenever the selection changes.
     *
     * @param {Event} e - Change event containing the selected cost type value.
     */
    const onCostChange = (e) => {
        const value = e?.target?.value;

        let nextState = {
            isMealTimeCost: false,
            isBaseCost: false,
            baseCost: 0
        };

        switch (value) {
            case IS_MEAL_TIME_COST_NAME:
                nextState.isMealTimeCost = true;
                break;

            case IS_BASE_COST_NAME:
                nextState.isBaseCost = true;
                break;

            case IS_CATALOGUE_COST_NAME:
            default:
                break;
        }

        setItem((prev) => ({
            ...prev,
            ...nextState
        }));
        setIsValidCost(true);
        setIsValidCostText('');
    };

    return {
        handleChange,
        item,
        onChange: (e) => onChange(e),
        emptyFields,
        pricePlaceholder,
        isValidSelectionMenuDelay,
        isValidCost,
        isValidCostText,
        isValidSubsidy,
        isValidSubsidyText,
        isValidExtraMealSubsidy,
        isValidExtraMealSubsidyText,
        onCostChange
    }

}

export default SettingsApp;
